/* eslint-disable */
/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

/**
 * Parses European number format to cents.
 * Handles formats like "1.699,00" -> 169900 cents
 * @param {string} formattedPrice - The formatted price string like "1.699,00"
 * @returns {number} - The price in cents
 */
function parseEuropeanPriceToCents(formattedPrice) {
  if (!formattedPrice) return 0;

  // Remove currency symbols and spaces
  let cleanPrice = formattedPrice.replace(/[^\d.,]/g, "");

  // Handle European format: "1.699,00" where . is thousands separator and , is decimal
  if (cleanPrice.includes(".") && cleanPrice.includes(",")) {
    // Remove thousands separators (.) and replace decimal separator (,) with .
    cleanPrice = cleanPrice.replace(/\./g, "").replace(",", ".");
  } else if (cleanPrice.includes(",") && !cleanPrice.includes(".")) {
    // Only decimal separator: "1699,00" -> "1699.00"
    cleanPrice = cleanPrice.replace(",", ".");
  }
  // If only periods, assume it's already in correct format

  const priceValue = parseFloat(cleanPrice) || 0;
  return Math.round(priceValue * 100); // Convert to cents
}

/**
 * Formats a price in cents to European format with thousands separator and decimal places.
 * @param {number} priceInCents - The price in cents
 * @returns {string} - The formatted price string like "1.699,00"
 */
function formatEuropeanPrice(priceInCents) {
  if (!priceInCents || priceInCents === 0) return "0,00";

  const priceValue = priceInCents / 100;
  const formattedPrice = priceValue.toFixed(2);

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = formattedPrice.split(".");

  // Add thousands separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  // Return with comma as decimal separator
  return `${formattedInteger},${decimalPart}`;
}

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      /**
       * The this.section variable has to be kept in the global scope because
       * the majority of functions within this module (and potentially
       * other dependent scripts) rely on it being defined at a global level.
       * @author Benny Ackermann <b.ackermann@ivario.com>
       * @date 2025-08-28
       */
      this.section = this.closest('.js-product');
      // Initialize the product form if provided.
      this.productFormId = this.getAttribute("data-product-form-id");
      if (this.productFormId) {
        this.productForm = document.getElementById(this.productFormId);
      }
      // Fallback to main PDP form if not defined.
      if (!this.productForm && this.section) {
        this.productForm = this.section.querySelector(
          ".js-product-form-main"
        );
      }

      // Determine if this picker is inside a cross‑sell or upsell popup.
      this.isCrossSell =
        !!this.closest("qa-cross-sell") || !!this.closest("cross-sell-popup");
      this.isUpsell =
        !!this.closest("qa-upsell") || !!this.closest("upsell-popup");
      // When in a popup, do not reference the main PDP form.
      if (this.isCrossSell || this.isUpsell) {
        this.productForm = null;
      }

      this.optionSelectors = this.querySelectorAll(".option-selector");
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener("change", this.handleVariantChange.bind(this));

      setTimeout(() => {
        // Select first variant if requested and no variant is currently selected
        if (this.dataset.selectFirstVariant === "true" && !this.variant) {
          this.selectFirstAvailableVariant();
        }
        this.updateAvailability();
        this.updateAddToCartButton();
        // Ensure price and sale label reflect current quantity/tier on load
        if (!this.isCrossSell && !this.isUpsell) {
          this.updatePrice();
        }
      });
    }
    /**
     * Update the visible sale label text with the given percent, preserving icon and wording.
     * Works by replacing the first numeric token in the existing text node.
     * @param {number} percent
     */
    updateSaleLabelText(percent) {
      const variantLabelComponent =
        this.section && this.section.querySelector("variant-label");
      if (!variantLabelComponent || !this.variant) return;
      const currentVariantLabel = variantLabelComponent.querySelector(
        `[data-variant-id="${this.variant.id}"]`
      );
      if (!currentVariantLabel) return;
      const saleEl = currentVariantLabel.querySelector(".product-label--sale");
      if (!saleEl) return;

      if (saleEl.dataset.discountTemplate) {
        const template = saleEl.dataset.discountTemplate;
        const saleLabelText = saleEl.querySelector("[data-sale-label-text]");
        if (saleLabelText) {
          saleLabelText.textContent = template
            .replace("__DISCOUNT__", Math.round(percent))
            .replace(/\s+/g, " ")
            .trim();
        }
      } else {
        const textNode = Array.from(saleEl.childNodes).find(
          (n) =>
            n.nodeType === 3 && n.textContent && n.textContent.trim().length > 0
        );
        const space = " ";
        const newNum = String(Math.round(percent));
        if (textNode) {
          const original = textNode.textContent;
          const replaced = original.replace(/[0-9]+(?:[.,][0-9]+)?/, newNum);
          textNode.textContent = replaced;
        } else {
          saleEl.appendChild(document.createTextNode(space + newNum + "%"));
        }
      }
    }

    hideSaleLabel() {
      const variantLabelComponent =
        this.section && this.section.querySelector("variant-label");
      if (!variantLabelComponent || !this.variant) return;
      const currentVariantLabel = variantLabelComponent.querySelector(
        `[data-variant-id="${this.variant.id}"]`
      );
      if (!currentVariantLabel) return;
      const saleLabel = currentVariantLabel.querySelector(
        ".product-label--sale"
      );
      if (saleLabel) saleLabel.setAttribute("hidden", "");
    }

    showSaleLabel() {
      const variantLabelComponent =
        this.section && this.section.querySelector("variant-label");
      if (!variantLabelComponent || !this.variant) return;
      const currentVariantLabel = variantLabelComponent.querySelector(
        `[data-variant-id="${this.variant.id}"]`
      );
      if (!currentVariantLabel) return;
      const saleLabel = currentVariantLabel.querySelector(
        ".product-label--sale"
      );
      if (saleLabel) saleLabel.removeAttribute("hidden");
    }

    /**
     * Returns the highest applicable discount percent for a given quantity from product tiers.
     * @param {number} qty
     * @returns {number}
     */
    getTierPercentForQty(qty) {
      try {
        const tiers = (this.data && this.data.discountTiers) || [];
        if (!Array.isArray(tiers)) return 0;
        let best = 0;
        for (let i = 0; i < tiers.length; i += 1) {
          const tier = tiers[i] || {};
          const minCandidate =
            tier.min_qty != null
              ? tier.min_qty
              : tier.minQty != null
              ? tier.minQty
              : tier.min != null
              ? tier.min
              : 0;
          const minQty = parseInt(minCandidate, 10) || 0;
          const percentCandidate =
            tier.percent != null
              ? tier.percent
              : tier.percentage != null
              ? tier.percentage
              : 0;
          const percent = parseFloat(percentCandidate) || 0;
          if (qty >= minQty && percent > best) best = percent;
        }
        return best;
      } catch (e) {
        return 0;
      }
    }

    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      // Only update the PDP price display if not in a popup.
      if (!this.isCrossSell && !this.isUpsell) {
        this.updatePrice();
      }
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(
          new CustomEvent("on:variant:change", {
            bubbles: true,
            detail: {
              form: this.productForm, // Will be null in upsell/cross‑sell mode.
              variant: this.variant,
              product: this.data.product,
            },
          })
        );
      }
    }

    updateMetafieldVisibility() {
      var nodes = document.querySelectorAll("[data-variant-metafield]");
      nodes.forEach(
        function (elem) {
          var vid =
            this.variant && this.variant.id != null
              ? this.variant.id.toString()
              : "";
          elem.classList.toggle(
            "hidden",
            elem.dataset.variantMetafield !== vid
          );
        }.bind(this)
      );
    }

    updateAddToCartButton() {
      // Only update PDP add-to-cart button if not in a popup.
      if (this.isCrossSell || this.isUpsell || !this.productForm) {
        return;
      }
      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      this.addBtn.disabled = !variantAvailable || this.preSelection;
      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant
          ? theme.strings.noStock
          : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);
      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");
        if (em) {
          em.hidden = available;
        }
        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      const { availabilityMethod } = this.dataset;
      let currVariant = this.variant || { options: this.selectedOptions };
      if (availabilityMethod === "selection") {
        this.querySelectorAll(".js-option").forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;
            variant.options.forEach((option, optionIndex) => {
              if (
                option === currVariant.options[optionIndex] &&
                optionIndex !== selectorIndex
              ) {
                matchCount += 1;
              }
            });
            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === "dropdown") {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });
              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i++) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });
        });
      }
    }

    updateBackorderText() {
      this.backorder =
        this.backorder ||
        (this.section && this.section.querySelector(".backorder"));
      if (!this.backorder) return;
      let hideBackorder = true;
      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id.toString()];
        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product"
          );
          const prodTitleEl =
            this.section && this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;
          if (prodTitleEl) {
            backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          }
          hideBackorder = false;
        }
      }
      this.backorder.hidden = hideBackorder;
    }

    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;
      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;
      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    updateMedia() {
      if (!this.variant.featured_media) return;
      if (this.section && this.section.matches("quick-add-drawer")) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery =
          this.mediaGallery ||
          (this.section && this.section.querySelector("media-gallery"));
        if (!this.mediaGallery) return;
        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        (this.section && this.section.querySelector("pickup-availability"));
      if (!this.pickUpAvailability) return;
      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    updatePrice() {
      // For variant pickers in popups (cross‑sell or upsell), skip price update.
      if (this.isCrossSell || this.isUpsell) return;

      // Don't cache the price element - find it fresh each time
      const priceElement = document.querySelector(
        ".product-info__price > .price"
      );
      if (!priceElement) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) {
            variant = this.data.product.variants[i];
          }
        }
      }
      if (this.variant) {
        const priceCurrentEl = priceElement.querySelector(".price__current");
        const priceWasEl = priceElement.querySelector(".price__was");
        const unitPriceEl = priceElement.querySelector(".unit-price");
        // Get current quantity to calculate correct price
        let qty = 1;
        if (!this.isCrossSell && this.productForm) {
          const qtyInput = this.productForm.querySelector(".qty-input__input");
          if (qtyInput) {
            qty = parseInt(qtyInput.value, 10);
          }
        }

        const tierPercent = this.getTierPercentForQty(qty);
        if (tierPercent > 0) {
          this.updateDiscountedPrice(priceCurrentEl, priceWasEl);
          this.updateSaleLabelText(tierPercent);
          this.showSaleLabel();
        } else {
          this.hideSaleLabel();
          // Calculate price with quantity
          const price =
            this.data.formatted[this.variant.id].price_without_currency || 0;
          const currency =
            this.data.formatted[this.variant.id].price.split(" ")[1];
          const totalPrice = price * qty;

          if (totalPrice > 0) {
            priceCurrentEl.innerHTML = `${formatEuropeanPrice(
              totalPrice
            )} ${currency}`;
          } else {
            priceCurrentEl.innerHTML =
              this.data.formatted[this.variant.id].price;
          }

          if (priceWasEl) {
            const compareAtPrice =
              this.data.formatted[this.variant.id].compareAtPrice || "";
            if (compareAtPrice && qty > 1) {
              // Parse compare at price correctly
              const compareAtPriceInCents =
                parseEuropeanPriceToCents(compareAtPrice);
              const totalComparePrice = compareAtPriceInCents * qty;
              priceWasEl.innerHTML = `${formatEuropeanPrice(
                totalComparePrice
              )} ${currency}`;
            } else {
              priceWasEl.innerHTML = compareAtPrice;
            }
          }
        }
        if (this.variant.unit_price_measurement) {
          const valueEl = priceElement.querySelector(".unit-price__price");
          const unitEl = priceElement.querySelector(".unit-price__unit");
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;
          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }
        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        priceElement.classList.toggle(
          "price--on-sale",
          this.variant.compare_at_price > this.variant.price
        );
        priceElement.classList.toggle(
          "price--sold-out",
          !this.variant.available && !this.preSelection
        );
      }
      priceElement.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;
      priceElement.querySelector(".price__no-variant").hidden =
        this.variant || this.preSelection;
      const from = priceElement.querySelector(".price__from");
      if (from) {
        from.hidden = !this.preSelection;
      }
    }
    updateDiscountedPrice(priceCurrentEl, priceWasEl) {
      // Compute discount percent from tiers and quantity
      const variantKey = this.variant.id.toString();
      let price = this.data.formatted[variantKey].price_without_currency || 0;
      let currency = this.data.formatted[variantKey].price.split(" ")[1];
      let variantLabel =
        this.section && this.section.querySelector(".variant-label");
      let qty = 1;
      if (!this.isCrossSell && this.productForm) {
        const qtyInput = this.productForm.querySelector(".qty-input__input");
        if (qtyInput) {
          qty = parseInt(qtyInput.value, 10);
        }
      }
      const discountPercent = this.getTierPercentForQty(qty);
      const normalPrice = price * qty;
      // Apply discount to total price, not individual item price
      let discountedTotalPrice =
        normalPrice - normalPrice * (discountPercent / 100);
      const splittedPrice = discountedTotalPrice.toString().split(".");

      // Adjust price to match Shopify's cart behavior
      // Shopify always add 1 cent more to the discounted price in cart. 8720.1 ==> 8721
      if (splittedPrice.length > 1 && parseInt(splittedPrice[1], 10) > 0) {
        discountedTotalPrice = parseInt(splittedPrice[0], 10) + 1;
      }
      discountedTotalPrice /= 100;
      if (qty > 2) {
        // Apply discount pricing for quantities > 2
        priceCurrentEl.innerHTML = `${formatEuropeanPrice(
          discountedTotalPrice * 100
        )} ${currency}`;
        if (variantLabel) {
          // Show only the sale label for current variant
          const variantLabelComponent =
            this.section && this.section.querySelector("variant-label");
          if (variantLabelComponent) {
            const currentVariantLabel = variantLabelComponent.querySelector(
              `[data-variant-id="${this.variant.id}"]`
            );
            if (currentVariantLabel) {
              const saleLabel = currentVariantLabel.querySelector(
                ".product-label--sale"
              );
              if (saleLabel) saleLabel.removeAttribute("hidden");
            }
          } else {
            variantLabel.removeAttribute("hidden");
          }
        }
        // Update sale label percent text
        this.updateSaleLabelText(discountPercent);
        // Show was price for discount
        if (priceWasEl && normalPrice > 0) {
          priceWasEl.innerHTML = `${formatEuropeanPrice(
            normalPrice
          )} ${currency}`;
        }
      } else {
        // For quantities <= 2, show normal price multiplied by quantity
        if (normalPrice > 0) {
          priceCurrentEl.innerHTML = `${formatEuropeanPrice(
            normalPrice
          )} ${currency}`;
        } else {
          priceCurrentEl.innerHTML = this.data.formatted[variantKey].price;
        }

        // Handle compare at price for normal pricing
        if (priceWasEl) {
          const compareAtPrice =
            this.data.formatted[variantKey].compareAtPrice || "";
          if (compareAtPrice && qty > 1) {
            // Parse compare at price correctly and multiply by quantity
            const compareAtPriceInCents =
              parseEuropeanPriceToCents(compareAtPrice);
            const totalComparePrice = compareAtPriceInCents * qty;
            priceWasEl.innerHTML = `${formatEuropeanPrice(
              totalComparePrice
            )} ${currency}`;
          } else {
            priceWasEl.innerHTML = compareAtPrice;
          }
        }

        if (variantLabel) {
          // Hide only the sale label for current variant
          const variantLabelComponent =
            this.section && this.section.querySelector("variant-label");
          if (variantLabelComponent) {
            const currentVariantLabel = variantLabelComponent.querySelector(
              `[data-variant-id="${this.variant.id}"]`
            );
            if (currentVariantLabel) {
              const saleLabel = currentVariantLabel.querySelector(
                ".product-label--sale"
              );
              if (saleLabel) saleLabel.setAttribute("hidden", "");
            }
          } else {
            variantLabel.setAttribute("hidden", "");
          }
        }
      }
    }

    updateWeight() {
      this.weights =
        this.weights ||
        (this.section &&
          this.section.querySelectorAll(".product-info__weight"));
      if (!this.weights || this.weights.length === 0) return;
      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable
          ? this.data.formatted[this.variant.id.toString()].weight
          : "";
        weight.hidden = !weightAvailable;
      });
    }

    updateBarcode() {
      this.barcodes =
        this.barcodes ||
        (this.section &&
          this.section.querySelectorAll(".product-info__barcode-value"));
      if (!this.barcodes || this.barcodes.length === 0) return;
      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
        if (barcode.parentNode) {
          barcode.parentNode.hidden = !barcodeAvailable;
        }
      });
    }

    updateSku() {
      this.sku =
        this.sku ||
        (this.section && this.section.querySelector(".product-sku__value"));
      if (!this.sku) return;
      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      if (this.sku.parentNode) {
        this.sku.parentNode.hidden = !skuAvailable;
      }
    }

    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.variant.id}`
      );
    }

    updateVariantInput() {
      if (this.isCrossSell || this.isUpsell) {
        let container;
        if (this.isCrossSell) {
          container = this.closest(".cross-sell__product-item");
          if (container && this.variant) {
            const input = container.querySelector(".cross-sell__variant-id");
            if (input) {
              input.value = this.variant.id;
            }
          }
        } else if (this.isUpsell) {
          container = this.closest(".upsell__product-item");
          if (container && this.variant) {
            const input = container.querySelector(".upsell__variant-id");
            if (input) {
              input.value = this.variant.id;
            }
          }
        }
      } else if (this.productForm) {
        const input = this.productForm.querySelector('input[name="id"]');
        if (input && this.variant) {
          input.value = this.variant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }

    getSelectedOptions() {
      const selectedOptions = [];
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selectedText = selector
            .querySelector(".custom-select__btn")
            .textContent.trim();
          selectedOptions.push(
            selectedText === this.dataset.placeholderText ? null : selectedText
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });
      return selectedOptions;
    }

    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index])
      );
    }

    selectFirstAvailableVariant() {
      if (
        !this.data.product.variants ||
        this.data.product.variants.length === 0
      )
        return;

      // Find the first available variant
      const firstAvailableVariant =
        this.data.product.variants.find((v) => v.available) ||
        this.data.product.variants[0];

      if (!firstAvailableVariant) return;

      // Select the options for this variant
      firstAvailableVariant.options.forEach((optionValue, optionIndex) => {
        const selector = this.optionSelectors[optionIndex];
        if (!selector) return;

        if (selector.dataset.selectorType === "dropdown") {
          // Handle dropdown (custom-select)
          const option = selector.querySelector(
            `[data-value="${optionValue}"]`
          );
          if (option) {
            const customSelect = selector.querySelector("custom-select");
            if (customSelect && customSelect.selectOption) {
              customSelect.selectOption(option);
            }
          }
        } else {
          // Handle radio buttons
          const radioOption = selector.querySelector(
            `input[value="${optionValue}"]`
          );
          if (radioOption) {
            radioOption.checked = true;
            // Trigger change event to update the picker
            radioOption.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      });

      // Update the variant after selection
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
